import { Badge, Box, formatCount, IconButton, type IconButtonProps } from 'pixelize-design-library';
import { Bell } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { statusFill, surface } from '@core/Theme/tokens';
import { useAppTheme } from '@core/Theme/useAppTheme';

interface NotificationBellProps extends Omit<IconButtonProps, 'aria-label' | 'icon'> {
  needsYou: number;
  unread: number;
}

export const NotificationBell = forwardRef<HTMLButtonElement, NotificationBellProps>(
  function NotificationBell({ needsYou, unread, ...rest }, ref) {
    const { t } = useTranslation();
    const { colors } = useAppTheme();
    const ring = surface(colors);
    const showCount = needsYou > 0;
    const showDot = !showCount && unread > needsYou;

    return (
      <Box position='relative' display='inline-block'>
        <IconButton
          ref={ref}
          icon={<Bell size={20} />}
          variant='ghost'
          borderRadius='full'
          aria-label={t('aria.notificationsBell', { needsYou, unread })}
          data-testid='notification-bell'
          {...rest}
        />
        {showCount ? (
          <Badge
            position='absolute'
            top='-5px'
            right='-5px'
            minW='18px'
            h='18px'
            px='5px'
            borderRadius='full'
            border='2px solid'
            borderColor={ring}
            bg={statusFill(colors, 'warning')}
            color={colors.onSemantic.warning}
            fontSize='xs'
            fontWeight='700'
            lineHeight='14px'
            textAlign='center'
            data-testid='notification-bell-badge'
          >
            {formatCount(needsYou)}
          </Badge>
        ) : null}
        {showDot ? (
          <Box
            position='absolute'
            top='-1px'
            right='-1px'
            w='8px'
            h='8px'
            borderRadius='full'
            border='2px solid'
            borderColor={ring}
            bg={statusFill(colors, 'info')}
            data-testid='notification-bell-dot'
          />
        ) : null}
      </Box>
    );
  }
);

export default NotificationBell;
